import { Controller } from "stimulus";
import { isFinished } from "packs/engines/subscriber_v2/playback_database";

const VALUE_KEYS = ["itemId", "itemType", "feedItemUrl", "audioUrl", "navigateOnPlay", "name", "artworkUrl"];
const BUTTON_STATES = ["play", "loading", "pause", "locked"];
const datasetKey = (key) => `playButton${key[0].toUpperCase()}${key.slice(1)}Value`;

// Once this device has started the season, points the hero's Play at the first episode in season order not yet finished.
export default class SeasonHeroResumeController extends Controller {
  connect() {
    this.playButton = this.element.querySelector('[data-controller="play-button"]');
    if (!this.playButton) return;

    this.cached = false;
    this.original = {
      values: VALUE_KEYS.map((key) => [datasetKey(key), this.playButton.dataset[datasetKey(key)]]),
      labels: this.labelTargets.map((label) => [label, label.textContent]),
      states: this.stateTargets().map((target) => [target, target.hidden]),
    };
    this.boundRestore = this.beforeCache.bind(this);
    document.addEventListener("turbo:before-cache", this.boundRestore);
    this.resume();
  }

  disconnect() {
    if (this.boundRestore) document.removeEventListener("turbo:before-cache", this.boundRestore);
  }

  async resume() {
    if (!window.playbackDb) return;

    let progress;
    try {
      progress = await window.playbackDb.getPlaybackDataMany(this.episodesValue);
    } catch (error) {
      return;
    }
    if (!this.element.isConnected || this.cached || this.playbackStarted()) return;

    const index = progress.some((data) => data && data.currentTime > 0)
      ? progress.findIndex((data) => !(data && isFinished(data)))
      : -1;
    if (index >= 0) this.retarget(this.episodesValue[index]);
    else this.restore();
  }

  stateTargets() {
    return BUTTON_STATES.flatMap((name) => [...this.playButton.querySelectorAll(`[data-play-button-target="${name}"]`)]);
  }

  // Turbo clones the DOM a tick after this fires, so a late resume must not retarget the snapshot.
  beforeCache() {
    this.cached = true;
    this.restore();
  }

  // Playing or loading already: changing the button now would swap the episode under the viewer.
  playbackStarted() {
    return ["pause", "loading"].some((name) => {
      const target = this.playButton.querySelector(`[data-play-button-target="${name}"]`);
      return target && !target.hidden;
    });
  }

  retarget(episode) {
    const values = { ...episode, name: episode.title };
    VALUE_KEYS.forEach((key) => { this.playButton.dataset[datasetKey(key)] = values[key]; });
    this.labelTargets.forEach((label) => { label.textContent = `Continue: ${episode.title}`; });
  }

  // Puts the server-rendered button back, so a Turbo cache snapshot never holds a stale Continue.
  restore() {
    this.original.values.forEach(([key, value]) => {
      if (value === undefined) delete this.playButton.dataset[key];
      else this.playButton.dataset[key] = value;
    });
    this.original.labels.forEach(([label, text]) => { label.textContent = text; });
    this.original.states.forEach(([target, hidden]) => { target.hidden = hidden; });
  }
}

SeasonHeroResumeController.values = { episodes: Array };
SeasonHeroResumeController.targets = ["label"];
